import React, { useState, useEffect, useRef } from "react";
import { Route, Routes, BrowserRouter } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@chakra-ui/react";
import AppHeader from "./components/AppHeader";
import HomePage from "pages/landing/HomePage";
import Footer from "components/Footer";
import Dashboard from "pages/Dashboard";
import WritingPracticePage from "pages/writing/WritingPracticePage";
import BrainstormForm from "pages/writing/BrainstormForm";
import DraftingForm from "pages/writing/DraftingForm";
import PlanningForm from "pages/writing/PlanningForm";
import RevisingForm from "pages/writing/RevisingForm";
import EditingForm from "pages/writing/EditingForm";
import Assessment from "pages/writing/Assessment";
import Signup from "pages/auth/SignUp";
import SignupConfirmation from "pages/auth/SignupConfirmation";
import Signin from "pages/auth/SignIn";
import PasswordReset from "pages/auth/ResetPassword";
import PasswordUpdate from "pages/auth/UpdatePassword";
// import StudentSettingsForm from 'pages/student/StudentSettings';
// import StudentDashboard from 'pages/student/StudentDashboard';
import StudentProfile from "pages/student/StudentProfile";
import StudentAssignmentPage from "pages/student/StudentAssignmentPage";
import StudentMessagePage from "pages/student/StudentMessagePage";
import StudentEssayPage from "pages/student/StudentEssayPage";
import StudentReportPage from "pages/student/StudentReportPage";
import CreateEssayPromptPage from "pages/prompt/CreateEssayPromptPage";
import EssayPromptDetailPage from "pages/prompt/EssayPromptDetailPage";
import SettingsPage from "pages/settings/SettingsPage";
import PricingPage from "pages/subscription/Pricing";
import SubscriptionSuccess from "pages/subscription/SubscriptionSuccess";
import SubscriptionFailure from "pages/subscription/SubscriptionFailure";

import { logInSuccess, refreshToken } from "./redux/slices/userSlice";
import api from "api/axiosConfig";
import API_BASE from "configs/hostConfig";
import AccessDenied from "components/AccessDenied";
import ServiceExpired from "components/PlanExpired";
import StoryRelayPage from "pages/relay/StoryRelayPage";
import CreateRelayPromptPage from "pages/prompt/CreateRelayPromptPage";
import RelayPromptDetailPage from "pages/prompt/RelayPromptDetailPage";
import StudentStorytellingPage from "pages/student/StudentStorytellingPage";
import ManagementCenter from "pages/management/ManagementCenter";
import EssayManagement from "pages/management/EssayManagement";
import RelayStoryManagement from "pages/management/RelayStoryManagement";
import Payments from "pages/management/Payments";
import UserManagement from "pages/management/UserManagement";
import RelayPromptManagement from "pages/management/RelayPromptManagement";
import EssayPromptManagement from "pages/management/EssayPromptManagement";
import CreateBlogPage from "pages/blog/CreateBlogPage";
import EditBlogPage from "pages/blog/EditBlogPage";
import BlogPage from "pages/blog/BlogPage";
import BlogListPage from "pages/blog/BlogListPage";
import BlogManagement from "pages/management/BlogManagement";
import PrivacyPolicy from "pages/support/PrivacyPolicy";
import TermsOfService from "pages/support/TermsOfService";
// import ContactUs from 'pages/support/ContactUs';
import Tutorial from "pages/support/Tutorial";
import StudentDiaryPage from "pages/student/StudentDiaryPage";
import CreateDiaryPage from "pages/diary/CreateDiaryPage";
import DiaryDetailPage from "pages/diary/DiaryDetailPage";

function App() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.info);
  const [isScrolled, setIsScrolled] = useState(false);
  const mainRef = useRef();

  // Monitor scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (mainRef.current) {
        const scrollTop = mainRef.current.scrollTop;
        setIsScrolled(scrollTop > 0); // Update state based on scroll position
      }
    };

    const mainElement = mainRef.current;
    mainElement.addEventListener("scroll", handleScroll);

    return () => {
      if (mainElement) {
        mainElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    const sessionId = localStorage.getItem("session_id");
    if (sessionId && !user) {
      dispatch(refreshToken(sessionId));
      api
        .get(`${API_BASE}/api/user`)
        .then(({ data }) => {
          console.log("app get user response data: ", data);
          dispatch(logInSuccess(data));
        })
        .catch((err) => console.log("failed to sign in reason: ", err));
    }
  }, [dispatch, user]);

  return (
    <BrowserRouter>
      <AppHeader isScrolled={isScrolled} />
      <Box
        display="flex"
        flexDirection="column"
        minHeight="100vh" // Ensure the page takes full height
        overflowX="hidden" // Prevent horizontal scroll
        overflowY="auto"
        ref={mainRef}
      >
        <Box as="main" flex="1">
          <Routes>
            {/* <Route path="/prompt-card" element={<PromptCard />} /> */}
            <Route path="/" element={<HomePage />} />
            {/* <Route path="/test" element={<DraftForm />} /> */}
            <Route path="/signup" element={<Signup />} />
            <Route
              path="/signup-confirmation"
              element={<SignupConfirmation />}
            />
            <Route path="/verified" element={<SignupConfirmation />} />
            <Route path="/signin" element={<Signin />} />
            <Route path="/reset-password" element={<PasswordReset />} />
            <Route path="/update-password" element={<PasswordUpdate />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/access-denied" element={<AccessDenied />} />
            <Route path="/service-expired" element={<ServiceExpired />} />
            <Route
              path="/subscription-success"
              element={<SubscriptionSuccess />}
            />
            <Route
              path="/subscription-failure"
              element={<SubscriptionFailure />}
            />
            {/* <Route path="/student-settings" element={<StudentSettingsForm />} /> */}
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/admin/management" element={<ManagementCenter />}>
              <Route
                path="/admin/management/blogs"
                element={<BlogManagement />}
              />
              <Route
                path="/admin/management/essay-prompt"
                element={<EssayPromptManagement />}
              />
              <Route
                path="/admin/management/essay"
                element={<EssayManagement />}
              />
              <Route
                path="/admin/management/relay-prompt"
                element={<RelayPromptManagement />}
              />
              <Route
                path="/admin/management/story"
                element={<RelayStoryManagement />}
              />
              <Route path="/admin/management/payments" element={<Payments />} />
              <Route
                path="/admin/management/users"
                element={<UserManagement />}
              />
            </Route>
            {/* <Route path="/student-dashboard/" element={<StudentDashboard />} /> */}
            <Route path="/student-profile" element={<StudentProfile />}>
              <Route
                path="/student-profile/dashboard"
                element={<StudentProfile />}
              />
              <Route
                path="/student-profile/assignment"
                element={<StudentAssignmentPage />}
              />
              <Route
                path="/student-profile/message"
                element={<StudentMessagePage />}
              />
              <Route
                path="/student-profile/essay"
                element={<StudentEssayPage />}
              />
              <Route
                path="/student-profile/storytelling"
                element={<StudentStorytellingPage />}
              />
              <Route
                path="/student-profile/diary"
                element={<StudentDiaryPage />}
              />
              <Route
                path="/student-profile/reports"
                element={<StudentReportPage />}
              />
            </Route>
            <Route
              // path="/writing-practice/:esayId"
              element={<WritingPracticePage />}
            >
              <Route
                path="/writing-practice/:essayId/brainstorming"
                element={<BrainstormForm />}
              />
              <Route
                path="/writing-practice/:essayId/planning"
                element={<PlanningForm />}
              />
              <Route
                path="/writing-practice/:essayId/drafting"
                element={<DraftingForm />}
              />
              <Route
                path="/writing-practice/:essayId/revising"
                element={<RevisingForm />}
              />
              <Route
                path="/writing-practice/:essayId/editing"
                element={<EditingForm />}
              />
              <Route
                path="/writing-practice/:essayId/assessment"
                element={<Assessment />}
              />
            </Route>
            <Route
              path="/create-essay-prompt"
              element={<CreateEssayPromptPage />}
            />
            <Route
              path="/essay-prompt/:promptId"
              element={<EssayPromptDetailPage />}
            />
            <Route
              path="/impromptu-story/:storyId"
              element={<StoryRelayPage />}
            />
            <Route
              path="/create-relay-prompt"
              element={<CreateRelayPromptPage />}
            />
            <Route
              path="relay-prompt/:promptId"
              element={<RelayPromptDetailPage />}
            />
            <Route
              path="/new-diary"
              element={<CreateDiaryPage />}
            />
            <Route
              path="/diary/:diaryId"
              element={<DiaryDetailPage />}
            />
            <Route path="/create-blog" element={<CreateBlogPage />} />
            <Route path="/edit-blog/:blogId" element={<EditBlogPage />} />
            <Route path="/blog/:slug" element={<BlogPage />} />
            <Route path="/blogs" element={<BlogListPage />} />
            <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="/legal/terms-of-service"
              element={<TermsOfService />}
            />
            {/* <Route path="/contact-us" element={<ContactUs />} /> */}
            <Route path="/tutorial" element={<Tutorial />} />
          </Routes>
        </Box>
        <Footer />
      </Box>
    </BrowserRouter>
  );
}

export default App;
